import {useMemo} from 'react'
import {useLocation} from 'react-router-dom'
import {useQuery} from '@tanstack/react-query'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {buildDecisionSearchParams} from '../utils/optimizely-attributes'
import {getFxAudiencePage, readOptlyQa} from '../utils/optimizely-fx-page-context'
import {getVisitorId} from '../utils/optimizely-visitor-id'
import {getVisitorContext, setServerVisitorContext} from '../utils/optimizely-visitor-context'
import {useOptimizelyFxConfig} from './use-optimizely-fx-config'
import {useSsrFetchOrigin} from './use-ssr-fetch-origin'

const REQUEST_TIMEOUT_MS = 2000
const PENDING = Object.freeze({status: 'pending', decisions: {}})
const UNAVAILABLE = Object.freeze({status: 'unavailable', decisions: {}})

/*
 * This shopper's A/B variations from the uncached /cms-optimizely-decisions
 * endpoint (events disabled there — impressions fire on render). Once per
 * (visitor, route, optly_qa) tuple: staleTime is infinite, and pathname /
 * page_type / optly_qa are in the query key so a navigation re-buckets.
 * Called from AudienceProvider only. Never runs during SSR; identity rides in
 * headers, never the URL; waits for the customer groups to settle so a
 * logged-in shopper is bucketed once, not twice.
 *
 * Returns {status: 'pending' | 'resolved' | 'unavailable', decisions, knownFlagKeys, pathname}.
 * `unavailable` = will not resolve this session (switch off, no visitor id,
 * endpoint or SDK down) → the filter fails open. `pathname` is the path the
 * resolved decisions were fetched for (prepaint must not reuse them elsewhere).
 */
export const useOptimizelyDecisions = ({
    hydrated,
    isLoggedIn,
    customerGroupsCsv,
    loyaltyTier = '',
    settled
}) => {
    const {enabled, brandSlug, localeId} = useOptimizelyFxConfig()
    const {pathname, search} = useLocation()
    const page = getFxAudiencePage(pathname)
    const optlyQa = readOptlyQa(search)
    const {token: accessToken} = useAccessToken()
    const fetchOrigin = useSsrFetchOrigin()

    const visitorId = hydrated ? getVisitorId() : null
    const canResolve = hydrated && enabled && settled && !!visitorId

    const query = useQuery({
        queryKey: [
            'cms-optimizely-decisions',
            brandSlug,
            localeId,
            visitorId,
            isLoggedIn,
            customerGroupsCsv,
            loyaltyTier,
            page.pathname,
            page.page_type,
            optlyQa
        ],
        queryFn: async () => {
            const controller = new AbortController()
            const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
            try {
                const params = buildDecisionSearchParams({
                    brand: brandSlug,
                    locale: localeId,
                    pathname: page.pathname,
                    pageType: page.page_type,
                    optlyQa
                })
                const headers = {
                    'x-visitor-id': visitorId,
                    'x-is-logged-in': isLoggedIn ? 'true' : 'false',
                    // Bucketing attributes the browser knows; the server adds geo.
                    'x-visitor-context': JSON.stringify(getVisitorContext())
                }
                if (accessToken) headers.authorization = `Bearer ${accessToken}`
                if (customerGroupsCsv) headers['x-customer-groups'] = customerGroupsCsv
                if (loyaltyTier) headers['x-loyalty-tier'] = loyaltyTier

                const res = await fetch(`${fetchOrigin}/cms-optimizely-decisions?${params}`, {
                    headers,
                    signal: controller.signal
                })
                if (!res.ok) throw new Error(`cms-optimizely-decisions ${res.status}`)
                const body = await res.json()
                setServerVisitorContext(body?.serverContext)
                return {...body, requestedPathname: page.pathname}
            } finally {
                clearTimeout(timer)
            }
        },
        enabled: canResolve,
        staleTime: Infinity,
        cacheTime: 30 * 60 * 1000,
        retry: 1,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false
    })

    const {data, isError, isSuccess} = query
    return useMemo(() => {
        if (!hydrated) return PENDING
        if (!enabled || !visitorId || isError) return UNAVAILABLE
        if (!settled || !isSuccess) return PENDING
        if (!data || data.enabled === false || data.degraded) return UNAVAILABLE
        return {
            status: 'resolved',
            decisions: data.decisions || {},
            knownFlagKeys: Array.isArray(data.knownFlagKeys) ? data.knownFlagKeys : undefined,
            pathname:
                typeof data.requestedPathname === 'string' ? data.requestedPathname : undefined
        }
    }, [hydrated, enabled, visitorId, settled, isError, isSuccess, data])
}
